import { ApiError, RequestAbortError, type ConversationSummary, type TaskStatus } from '../../api/client.js';
import { h, toast, confirmModal, type View } from '../components/dom.js';
import { renderHeader } from '../components/header.js';
import type { SessionController } from '../../session/controller.js';
import { accountEvents, type AccountEvents } from '../account-events.js';

type Conversation = ConversationSummary;
type Filter = 'all' | 'device' | 'app';
const FILTERS: Array<[Filter, string]> = [['all', 'Todas'], ['device', 'ASIS One'], ['app', 'Esta app']];
const STATUS_TEXT: Record<TaskStatus, string> = { queued: 'En cola', running: 'Trabajando', needs_input: 'Necesita tu respuesta', succeeded: 'Terminado', failed: 'No se pudo terminar', cancelled: 'Cancelado', interrupted: 'Interrumpido' };
const working = (c: Conversation) => c.task?.status === 'queued' || c.task?.status === 'running';
/** Where it came from: the unit (by voice, or a task asked by voice) or this app. */
export function originLabel(c: Pick<Conversation, 'deviceId' | 'kind'>): string {
  if (c.kind === 'task') return c.deviceId ? 'Encargo · ASIS One' : 'Encargo · app';
  return c.deviceId ? 'Voz · ASIS One' : 'App';
}
type Message = { id: string; role: 'user' | 'assistant'; text: string; interrupted: boolean; createdAt: string };

export function createActividadView(controller: SessionController, options: { events?: AccountEvents } = {}): View {
  let active = true;
  let filter: Filter = 'all';
  const lifetime = new AbortController();
  const root = h('section', { class: 'view view-actividad', id: 'view-actividad' });
  root.appendChild(renderHeader('actividad'));
  const body = h('div', { class: 'section-body split' });
  root.appendChild(body);
  const refreshButton = h('button', { class: 'btn ghost refresh', type: 'button', hidden: true }, ['Reintentar']);
  refreshButton.addEventListener('click', () => { void refresh(); });
  root.querySelector('.topbar-right')?.append(refreshButton);

  const listCard = h('section', { class: 'card col-list' });
  listCard.append(h('h2', { class: 'card-title' }, ['Conversaciones']));
  const filterRow = h('div', { class: 'form-row conv-filters', role: 'group', 'aria-label': 'Origen de las conversaciones' });
  const filterButtons = FILTERS.map(([value, label]) => {
    const button = h('button', { class: 'btn ghost small', type: 'button', 'aria-pressed': String(value === filter) }, [label]);
    button.addEventListener('click', () => {
      if (filter === value) return;
      filter = value;
      for (const [index, item] of filterButtons.entries()) item.setAttribute('aria-pressed', String(FILTERS[index]![0] === filter));
      void refresh();
    });
    return button;
  });
  filterRow.append(...filterButtons);
  // Parallel tasks still working: each one has its own conversation and says so here until it ends.
  const runningHost = h('div', { class: 'conv-running', hidden: true }, [h('h3', { class: 'card-sub' }, ['En curso'])]);
  const running = h('ul', { class: 'conv-list', 'aria-label': 'Encargos en curso' });
  runningHost.append(running);
  const list = h('ul', { class: 'conv-list' });
  listCard.append(filterRow, runningHost, list);
  body.appendChild(listCard);

  const detailCard = h('section', { class: 'card col-detail' });
  detailCard.append(h('h2', { class: 'card-title' }, ['Detalle']));
  const detail = h('div', { class: 'conv-detail' }, [h('p', { class: 'muted' }, ['Selecciona una conversación para ver sus mensajes.'])]);
  detailCard.appendChild(detail);
  body.appendChild(detailCard);

  const api = controller.getApi();
  let conversations: Conversation[] = [];
  let selected: Conversation | null = null;
  let selectionGeneration = 0;
  let loading = false;

  let again = false;
  async function refresh(quiet = false): Promise<void> {
    if (!active) return;
    if (loading) { again = true; return; }
    loading = true;
    if (!quiet) list.replaceChildren(h('li', { class: 'muted' }, ['Cargando…']));
    try {
      const r = await api.listConversations(lifetime.signal, filter === 'all' ? {} : { origin: filter });
      if (!active) return;
      if (!active) return;
      refreshButton.hidden = true;
      conversations = r.conversations;
      renderList();
      const fresh = selected ? conversations.find(c => c.id === selected!.id) : undefined;
      if (fresh) await select(fresh);
      else if (selected) { ++selectionGeneration; selected = null; detail.replaceChildren(h('p', { class: 'muted' }, ['Esta conversación ya no está disponible.'])); }
    } catch (err) {
      if (!active || err instanceof RequestAbortError) return;
      refreshButton.hidden = false;
      list.replaceChildren(h('li', { class: 'muted' }, [`No se pudo cargar (${(err as Error).message})`]));
    } finally {
      loading = false;
      if (again && active) { again = false; void refresh(true); }
    }
  }

  function renderList(): void {
    list.replaceChildren();
    const busy = conversations.filter(working);
    runningHost.hidden = busy.length === 0;
    running.replaceChildren(...busy.map(c => {
      const open = h('button', { class: 'conversation-open', type: 'button' }, [
        h('div', { class: 'conv-title' }, [c.title || 'Encargo']),
        h('div', { class: 'conv-meta' }, [`${STATUS_TEXT[c.task!.status]} · ${originLabel(c)}${c.task!.progress ? ` · ${c.task!.progress.slice(0, 80)}` : ''}`]),
      ]);
      open.addEventListener('click', () => { void select(c); });
      return h('li', { class: 'conv-row conv-working' }, [open]);
    }));
    const rest = conversations.filter(c => !working(c));
    if (rest.length === 0) {
      if (busy.length) return;
      list.appendChild(h('li', { class: 'empty' }, [h('h3', {}, ['Aquí continúa tu historia']), h('p', {}, [filter === 'device' ? 'Todavía no hay conversaciones con ASIS One.' : 'Sin conversaciones registradas.']), h('a', { class: 'btn ghost', href: '#/conversar' }, ['Hablar con ASIS'])]));
      return;
    }
    for (const c of rest) {
      const li = h('li', { class: 'conv-row' });
      const title = h('div', { class: 'conv-title' }, [c.title || 'Sin título']);
      const state = c.task && !working(c) ? ` · ${STATUS_TEXT[c.task.status]}` : '';
      const meta = h('div', { class: 'conv-meta' }, [`${originLabel(c)}${state} · ${new Date(c.createdAt).toLocaleString()}`]);
      const del = h('button', { class: 'btn ghost small', type: 'button' }, ['Borrar']);
      del.addEventListener('click', () => { void doDelete(c); });
      const open = h('button', { class: 'conversation-open', type: 'button' }, [title, meta]);
      open.addEventListener('click', () => { void select(c); });
      li.append(open, del);
      list.appendChild(li);
    }
  }

  async function select(c: Conversation): Promise<void> {
    const generation = ++selectionGeneration;
    selected = c;
    detail.replaceChildren(h('p', { class: 'muted' }, ['Cargando…']));
    try {
      const r = await api.getConversation(c.id, lifetime.signal);
      if (!active || generation !== selectionGeneration) return;
      if (!active) return;
      detail.replaceChildren();
      detail.appendChild(h('h3', {}, [r.conversation.title || 'Sin título']));
      detail.appendChild(h('p', { class: 'muted' }, [`${originLabel(r.conversation)} · ${new Date(r.conversation.createdAt).toLocaleString()}`]));
      if (working(c)) detail.appendChild(h('p', { class: 'card-sub', role: 'status' }, ['Sigue trabajando en segundo plano. Te avisaremos al terminar.']));
      for (const m of r.messages) {
        const row = h('div', { class: `msg msg-${m.role}${m.interrupted ? ' interrupted' : ''}` });
        row.append(
          h('div', { class: 'msg-role' }, [m.role === 'user' ? 'Tú' : 'ASIS']),
          h('div', { class: 'msg-text' }, [m.text]),
        );
        if (m.interrupted) row.append(h('span', { class: 'tag' }, ['Interrumpida']));
        detail.appendChild(row);
      }
      if (r.messages.length === 0) detail.appendChild(h('p', { class: 'muted' }, ['Sin mensajes guardados.']));
    } catch (err) {
      if (!active || err instanceof RequestAbortError) return;
      if (generation !== selectionGeneration) return;
      refreshButton.hidden = false;
      detail.replaceChildren(h('p', { class: 'muted' }, [`No se pudo cargar (${(err as Error).message})`]));
    }
  }

  async function doDelete(c: Conversation): Promise<void> {
    const ok = await confirmModal({ title: 'Borrar conversación', body: 'Se eliminará la conversación y todos sus mensajes.', confirmLabel: 'Borrar', danger: true });
    if (!ok) return;
    try {
      await api.deleteConversation(c.id);
      if (!active) return;
      conversations = conversations.filter((x) => x.id !== c.id);
      if (selected && selected.id === c.id) { ++selectionGeneration; selected = null; detail.replaceChildren(h('p', { class: 'muted' }, ['Selecciona una conversación.'])); }
      renderList();
      toast('Conversación borrada', 'ok');
    } catch (err) {
      if (!active || err instanceof RequestAbortError) return;
      toast(`No se pudo borrar: ${apiMessage(err)}`, 'error');
    }
  }

  // A task changing state refreshes the list quietly (and the open thread when its answer lands).
  let pending: ReturnType<typeof setTimeout> | undefined;
  const stopEvents = (options.events ?? accountEvents).subscribe(event => {
    if (!active) return;
    clearTimeout(pending);
    pending = setTimeout(() => { void refresh(true); }, event.type === 'task.finished' ? 0 : 1500);
  });
  void refresh();
  return { el: root, destroy() { active = false; clearTimeout(pending); stopEvents(); lifetime.abort(); }, update() { void refresh(); } };
}

function apiMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  return (err as Error).message ?? 'error';
}
